import { useManagees } from "~/components/team-member/composables/useManagees";
import { useUserMe } from "~/components/user/composables/useUser";
import { useSupabase } from "~/composables/useSupabase";
import type { TeamMemberSelectionEntry, TeamMemberTabScope } from "@asap/shared/schemas/team-member-select";
import { userRoleWithTeamMemberColumns } from "@asap/shared/utils/supabase.columns";
import type { AppRole } from "@asap/shared/utils/supabase.enum";
import type { UserRoleWithTeamMember } from "@asap/shared/utils/supabase.types";
import { watchDebounced } from "@vueuse/core";
import type { ComputedRef, Ref } from "vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
  memberMatchesTabScope,
  PAGE_SIZE,
  parseTabValueToScope,
  type MemberMatchesScopeContext,
  type TeamMemberOption,
} from "~/components/team-member/team-member-select-v2.model";
import { useSelectMenuLoadMore } from "~/composables/useSelectMenuLoadMore";

type PropsLike = {
  roles?: AppRole[];
  value?: string | TeamMemberSelectionEntry[];
  excludeIds?: string[];
  lockedScope?: TeamMemberTabScope;
};

type SelectMenuRef = { getOptionsViewport: () => HTMLElement | null };

export function useTeamMemberSelectV2Data(params: {
  selectProps: PropsLike;
  isMultiple: ComputedRef<boolean>;
  selectedIdsArray: ComputedRef<string[]>;
  activeFilter: Ref<string>;
  defaultFilterValue: ComputedRef<string>;
  teamMemberSelectMenuRef: Ref<SelectMenuRef | null>;
}) {
  const { selectProps, isMultiple, selectedIdsArray, activeFilter, defaultFilterValue, teamMemberSelectMenuRef } =
    params;

  const supabase = useSupabase();
  const { ensureManageesLoaded, managees: allManagees } = useManagees();
  const { isIndependentAdministrator, isIndependent, isManager, isAccounting, workspaces, isCSM, teamMember } =
    useUserMe();

  const searchTerm = ref("");
  const selectedTeamMembers = ref<TeamMemberOption[]>([]);
  const manageeIds = ref<string[]>([]);
  const isManageesLoaded = ref(false);

  const getListViewport = (): HTMLElement | null => {
    return teamMemberSelectMenuRef.value?.getOptionsViewport() ?? null;
  };

  const showManagees = computed(() => activeFilter.value === "managees");
  const showConsultants = computed(() => activeFilter.value === "consultants");
  const showIndependents = computed(() => activeFilter.value === "independents");
  const showInactive = computed(() => activeFilter.value === "inactives");

  const buildQuery = () => {
    let query = supabase.from("user_role").select(userRoleWithTeamMemberColumns);

    if (
      !(
        isManager.value ||
        isIndependentAdministrator.value ||
        isAccounting.value ||
        isIndependent.value ||
        isCSM.value
      ) &&
      workspaces.value.length > 0
    ) {
      query = query.in(
        "auth_user.team_member.workspace_id",
        workspaces.value.map((workspace) => workspace.id)
      );
    }

    if (showManagees.value) {
      query = query.in("auth_user.team_member.id", manageeIds.value);
    } else if (showConsultants.value) {
      query = query.eq("auth_user.team_member.is_independent", false);
      query = query.is("auth_user.team_member.offboarded_at", null);
    } else if (showIndependents.value) {
      query = query.eq("auth_user.team_member.is_independent", true);
      query = query.is("auth_user.team_member.offboarded_at", null);
    } else if (showInactive.value) {
      query = query.not("auth_user.team_member.offboarded_at", "is", null);
    }

    if (searchTerm.value) {
      query = query.or(`first_name.ilike.%${searchTerm.value}%,last_name.ilike.%${searchTerm.value}%`, {
        foreignTable: "auth_user",
      });
      query = query.not("auth_user", "is", null);
    }

    if (selectProps.roles) {
      query = query.in("role", selectProps.roles);
    }

    return query;
  };

  const transformToOptions = (data: UserRoleWithTeamMember[] | null): TeamMemberOption[] => {
    if (!data) return [];

    const uniqueMap = new Map<string, UserRoleWithTeamMember>();
    for (const tm of data) {
      const teamMemberId = tm.auth_user.team_member.id;
      if (!uniqueMap.has(teamMemberId)) {
        uniqueMap.set(teamMemberId, tm);
      }
    }

    return Array.from(uniqueMap.values()).map((tm) => ({
      ...tm.auth_user.team_member,
      label: `${tm.auth_user.first_name} ${tm.auth_user.last_name}`,
      is_independent: tm.auth_user.team_member.is_independent,
    }));
  };

  const loadSelectedTeamMembers = async (teamMemberIds: string[]) => {
    const { data, error } = await supabase
      .from("user_role")
      .select(userRoleWithTeamMemberColumns)
      .in("auth_user.team_member.id", teamMemberIds);

    if (error) throw error;

    selectedTeamMembers.value = transformToOptions(data);
  };

  const loadPage = async (page: number, excludeIds: string[] = []): Promise<TeamMemberOption[]> => {
    const query = buildQuery();
    const isSearching = !!searchTerm.value;

    if (isSearching) {
      const { data, error } = await query.limit(500);
      if (error) throw error;

      const allOptions = transformToOptions(data).filter((tm) => !excludeIds.includes(tm.id));
      const start = page * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      return allOptions.slice(start, end);
    }

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE * 2 - 1;
    const { data, error } = await query.range(from, to);
    if (error) throw error;

    const allOptions = transformToOptions(data).filter((tm) => !excludeIds.includes(tm.id));
    return allOptions.slice(0, PAGE_SIZE);
  };

  const {
    items: availableOptions,
    isFirstLoad: loading,
    isLoadingMore: loadingMore,
    isRefreshing,
    hasMore,
    isInitialized: isMounted,
    initialize,
    refresh,
  } = useSelectMenuLoadMore<TeamMemberOption>({
    pageSize: PAGE_SIZE,
    selectMenuRef: teamMemberSelectMenuRef,
    loadPage: async (pageIndex, loadedItems) => {
      const excludeIds =
        pageIndex === 0
          ? selectedIdsArray.value
          : [...selectedIdsArray.value, ...loadedItems.map((teamMemberOption) => teamMemberOption.id)];
      return loadPage(pageIndex, excludeIds);
    },
  });

  watch(
    () => selectProps.value,
    async (newVal) => {
      const viewport = getListViewport();
      const scrollTop = viewport?.scrollTop ?? 0;

      const ids = isMultiple.value
        ? newVal === undefined || !Array.isArray(newVal)
          ? []
          : newVal.filter((e): e is { type: "id"; value: string } => e.type === "id").map((e) => e.value)
        : typeof newVal === "string" && newVal.length > 0
          ? [newVal]
          : [];

      const deselectedMembers = selectedTeamMembers.value.filter((tm) => !ids.includes(tm.id));

      if (ids.length > 0) {
        await loadSelectedTeamMembers(ids);
        availableOptions.value = availableOptions.value.filter((tm) => !ids.includes(tm.id));
      } else {
        selectedTeamMembers.value = [];
      }

      if (deselectedMembers.length > 0) {
        const tabScope = parseTabValueToScope(activeFilter.value);
        const memberScopeContext: MemberMatchesScopeContext = {
          manageeIds: manageeIds.value,
          isManageesLoaded: isManageesLoaded.value,
          selectedIdsFallbackForManagees: selectedIdsArray.value,
        };
        const toMerge =
          tabScope === null
            ? deselectedMembers
            : deselectedMembers.filter((tm) => memberMatchesTabScope(tm, tabScope, memberScopeContext));
        availableOptions.value = [...availableOptions.value, ...toMerge].sort((a, b) => a.label.localeCompare(b.label));
      }

      await nextTick();
      if (viewport) {
        viewport.scrollTop = scrollTop;
      }
    }
  );

  watch(activeFilter, async () => {
    if (!isMounted.value) return;
    await refresh();
  });

  watchDebounced(
    searchTerm,
    async () => {
      await refresh();
    },
    { debounce: 300 }
  );

  onMounted(async () => {
    await initialize(async () => {
      const initialTeamMemberIds = isMultiple.value
        ? selectedIdsArray.value
        : typeof selectProps.value === "string" && selectProps.value.length > 0
          ? [selectProps.value]
          : [];

      if (initialTeamMemberIds.length > 0) {
        await loadSelectedTeamMembers(initialTeamMemberIds);
      }

      activeFilter.value = defaultFilterValue.value;

      await ensureManageesLoaded();
      const selfId = teamMember.value?.id;
      const includeSelf = Boolean(selfId) && (isManager.value || selectProps.lockedScope === "managees");
      manageeIds.value = [...(includeSelf && selfId ? [selfId] : []), ...allManagees.value.map((m) => m.id)];
      isManageesLoaded.value = true;
    });
  });

  return {
    loading,
    hasMore,
    searchTerm,
    isMounted,
    loadingMore,
    isRefreshing,
    availableOptions,
    selectedTeamMembers,
    manageeIds,
    isManageesLoaded,
    getListViewport,
  };
}
